// Pure CSV/paste parser + validator for the PnP bulk-enroll importer.
// Kept dependency-free and side-effect-free so it is unit-testable in isolation
// (see ui-src/test/pnpCsv.test.ts) — the UI only wires it to a <textarea>.
//
// Accepts the documented column order `mac,sn,site,extension,label`, with:
//   - comma OR tab delimiters (spreadsheet + barcode-scanner paste),
//   - an optional header row (auto-detected and skipped),
//   - blank lines and `#`-comment lines (ignored),
//   - MACs in any separator style (aa:bb.., AABB-.., bare 12-hex).
// Each row is validated but NEVER silently dropped — invalid rows come back with
// their errors so the operator sees exactly which lines to fix.

export interface PnpCsvRow {
  line: number;        // 1-based source line number (for operator feedback)
  mac: string;         // normalized: 12 lowercase hex chars, no separators ('' if unparseable)
  macRaw: string;      // as typed
  sn: string;
  site: string;
  extension: string;
  label: string;
  valid: boolean;
  errors: string[];
}

export interface PnpCsvParseResult {
  rows: PnpCsvRow[];
  validCount: number;
  invalidCount: number;
}

const HEADER_TOKENS = new Set(['mac', 'sn', 'serial', 'serialnumber', 'site', 'extension', 'ext', 'label']);

/** Normalize a MAC to 12 lowercase hex chars, or '' if it isn't 12 hex digits. */
export function normalizeMac(raw: string): string {
  const hex = String(raw || '').toLowerCase().replace(/[^0-9a-f]/g, '');
  return hex.length === 12 ? hex : '';
}

/** Split a single line into cells on comma or tab; trims each cell and surrounding quotes. */
function splitCells(line: string): string[] {
  const delim = line.includes('\t') && !line.includes(',') ? '\t' : (line.includes(',') ? ',' : '\t');
  return line.split(delim).map(c => c.trim().replace(/^"(.*)"$/, '$1').trim());
}

/** Is this line the header row? (first two cells look like column names, no valid MAC) */
function looksLikeHeader(cells: string[]): boolean {
  if (cells.length < 2) return false;
  const a = cells[0].toLowerCase().replace(/[^a-z]/g, '');
  const b = cells[1].toLowerCase().replace(/[^a-z]/g, '');
  return HEADER_TOKENS.has(a) && HEADER_TOKENS.has(b) && !normalizeMac(cells[0]);
}

export function parsePnpCsv(text: string): PnpCsvParseResult {
  const rows: PnpCsvRow[] = [];
  const lines = String(text || '').split(/\r?\n/);
  let headerSkipped = false;
  const seen = new Set<string>();

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const trimmed = raw.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const cells = splitCells(raw);
    if (!headerSkipped && looksLikeHeader(cells)) { headerSkipped = true; continue; }
    headerSkipped = true; // only the first non-blank line can be a header

    const [macRaw = '', sn = '', site = '', extension = '', label = ''] = cells;
    const mac = normalizeMac(macRaw);
    const errors: string[] = [];
    if (!macRaw) errors.push('missing MAC');
    else if (!mac) errors.push(`invalid MAC "${macRaw}" (need 12 hex digits)`);
    else if (seen.has(mac)) errors.push('duplicate MAC in this batch');
    if (!sn) errors.push('missing SN');
    if (mac) seen.add(mac);

    rows.push({
      line: i + 1, mac, macRaw, sn, site, extension, label,
      valid: errors.length === 0, errors,
    });
  }

  const validCount = rows.filter(r => r.valid).length;
  return { rows, validCount, invalidCount: rows.length - validCount };
}
